import React, { useState } from "react";
import api from "@/api";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { ClockPlus, Lock } from "lucide-react";
import PlannedInput from "@/components/PlannedInput";
import { apiErrorMessage } from "@/utils/apiError";
import { expectedText, plannedDateOf } from "@/utils/planning";

/** The task's Planned value as chosen ("" for none); a Planned date set before planned_interval existed counts. */
const plannedOf = (task) => task?.planned_interval || task?.planned_date || "";

/**
 * "Plan task" on a task card: Agrolab's Planned value in any of the five ways (exact date, month position,
 * week, season, free text). The customer's expected date (the Time interval) is shown locked - it is set
 * when the task is created (Documentation/plans/planning-dates.md in agromate-api).
 */
export function TimeIntervalDialog({ trial, task, onTaskComplete }) {
  const [planned, setPlanned] = useState(plannedOf(task));
  const [open, setOpen] = useState(false);
  const [opened, setOpened] = useState(0); // a fresh Planned input (its mode) each time the dialog opens
  const [error, setError] = useState(null);

  const handleOpenChange = (next) => {
    setOpen(next);
    if (next) {
      setPlanned(plannedOf(task));
      setOpened((n) => n + 1);
      setError(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    // (free text as typed, trimmed here)
    const value = planned.trim();
    if (value === plannedOf(task)) {
      setOpen(false);
      return;
    }
    try {
      // Only the Planned value and its date: the customer's expected date is never sent from here
      await api.patch(`trials/${trial.id}/diaries/${task.id}`, {
        planned_interval: value || null,
        planned_date: plannedDateOf(value),
      });
      setOpen(false);
      if (onTaskComplete) onTaskComplete();
    } catch (err) {
      console.error("Error saving the planned date:", err);
      setError(apiErrorMessage(err, "Failed to save the planned date."));
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange} modal={false}>
      <DialogTrigger asChild>
        <Button variant="outline" aria-label="Plan task" title="Plan task">
          <ClockPlus />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>Plan task</DialogTitle>
          <DialogDescription>
            Agrolab's planned date - it may be outside the Time interval.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4">
          <div className="flex items-start gap-2 rounded-md border border-dashed p-3 text-sm" data-testid="expected-locked">
            <Lock className="mt-0.5 h-4 w-4 shrink-0 text-gray-500" aria-hidden="true" />
            <div>
              <div>
                <span className="font-medium">Time interval:</span> {expectedText(task) || "—"}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">Set from the customer's agreement when the task was created. Change it in the task, with a reason.</div>
            </div>
          </div>
          <div className="grid gap-2">
            <Label>Planned date</Label>
            <PlannedInput key={opened} value={planned} onChange={setPlanned} idPrefix="plan-task" />
          </div>
          {error && <p role="alert" className="text-sm text-red-600 dark:text-red-400">{error}</p>}
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">Cancel</Button>
          </DialogClose>
          <Button onClick={handleSubmit}>Save changes</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
